import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useHistory, useParams, Link} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {lightColor} from '@ey/styled-theme';
import {EYLogo} from '@ey/canvascoreservices';
import {isEmpty} from 'lodash';
import {labels, setLanguage} from '../../utils/utils';
import {modalSource, routesResources} from '../../utils/uiconstants';
import {ProjectSelect} from '../ProjectSelect/ProjectSelect';
import NavUser from './NavUser';
import {
	selectMetadata,
	selectAdminUser
} from '../../../features/admin/admin-selector';
import {toggleUnsavedModal} from '../UnsavedChangesModal/UnsavedChangesModal';
import {getFeatureConfigurations} from './NavActions';

const Nav = () => {
	const {cardId, partitionKey} = useParams();

	const user = useSelector((store) => store.user);

	const admin = useSelector(selectAdminUser);

	const metadata = useSelector(selectMetadata(cardId));

	const [isModalVisible, setModalVisibility] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	const dispatch = useDispatch();
	const history = useHistory();

	useEffect(() => {
		if (user) {
			setLanguage(user.languageIsoCode);
		}

		if (admin) {
			setLanguage(admin.languageIsoCode);
		}
	}, [user, admin]);

	useEffect(() => {
		getConfigFeatures();
	}, []);

	const getConfigFeatures = () => {
		dispatch(getFeatureConfigurations());
	};

	const validEconomicsUser = useMemo(() => {
		if (user && user.hasOwnProperty('id') && !isEmpty(user.id)) {
			return true;
		}
		return false;
	}, [user]);

	const validAdminUser = useMemo(() => {
		if (admin && admin.hasOwnProperty('id') && !isEmpty(admin.id)) {
			return true;
		}
		return false;
	}, [admin]);

	const {validUser, pathName} = useMemo(() => {
		return {
			validUser: validEconomicsUser ? validEconomicsUser : validAdminUser,
			pathName: validEconomicsUser ? '/' : `/${routesResources.admin}`
		};
	}, [validEconomicsUser, validAdminUser]);

	const onClick = useCallback(
		(event) => {
			event.preventDefault();

			const to = {
				pathname: `${pathName}`,
				state: {
					doNotReload: true,
					getAllProjects: true
				}
			};

			if (metadata?.hasChanges) {
				toggleUnsavedModal(modalSource.logo, {partitionKey});
			} else {
				history.push(to);
			}
		},
		[metadata, partitionKey, pathName, modalSource]
	);

	return (
		<StyledNavbar className="styled-nav-bar">
			<section className="mainResponsiveWrapper">
				<section className="nav-bar__ey-logo">
					{validUser ? (
						<Link onClick={onClick}>
							<EYLogo logoType="" />
							<h1 className="nav-bar__ey-logo__text">
								{labels.canvasEconomics}
							</h1>
						</Link>
					) : (
						<span>
							<EYLogo logoType="" />
							<h1 className="nav-bar__ey-logo__text">
								{labels.canvasEconomics}
							</h1>
						</span>
					)}
				</section>
				<ProjectSelect />
				<NavUser
					validEconomicsUser={validEconomicsUser}
					validAdminUser={validAdminUser}
					user={user}
					admin={admin}
					setIsOpen={setIsOpen}
					isOpen={isOpen}
					isModalVisible={isModalVisible}
					setModalVisibility={setModalVisibility}
					hasChanges={metadata?.hasChanges}
					partitionKey={partitionKey}
				/>
			</section>
		</StyledNavbar>
	);
};

export default Nav;

const StyledNavbar = styled.section`
	height: 6rem;
	width: 100%;
	border: none;
	background-color: ${lightColor[`grey500`]};
	display: flex;
	position: relative;
	box-shadow: 0 0.2rem 0.4rem 0 rgba(0,0,0,0.5);

	.mainResponsiveWrapper {
		display: flex;
		padding: 0 2.2rem;
	}

	.delegate-btn {
		display: inherit;
		&.motif-icon-button {
			background: transparent !important;
			&:hover {
				background: transparent !important;
			}
		}
		.user-initials {
			border-radius: 50%;
			width: 2.4rem;
			height: 2.4rem;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			line-height: 1.2rem;
			background-color: ${lightColor[`blue500`]};
			color: ${lightColor[`white`]};
			font-size: 1rem;
			font-weight: 700;
		}
	}

	.nav-bar__ey-logo {
		vertical-align: top;
		margin: 1.1rem 0 1.6rem 0;
		display: inline-flex;
		user-select: none;
		figure {
			height: 3.3rem;
			width: 3.1rem;
			vertical-align: unset;
		}
		.nav-bar__ey-logo__text {
			font-size: 2.4rem;
			display: inline-block;
			font-weight: 300;
			line-height: 2.4rem;
			margin: 1rem 1.2rem 0 0.8rem;
			color: ${lightColor[`white`]};
		}
	}

	.nav-bar_right-wrapper {
		margin-left: auto;
		.nav-bar__user-profile {
			position: relative;
			display: inline-flex;
			max-width: 30rem;
			width: auto;
			font-size: 1.4rem;
			height: 100%;
			align-items: center;

			.motif-dropdown .motif-dropdown-menu {
				margin-right: 0.2rem;
				margin-top: 1.3rem;
				z-index: 10001;
				padding: 0;
			}
			.motif-dropdown-menu[x-placement^='bottom-right'] {
				right: -2rem;
				&:before {
					right: 2rem;
				}
			}

			.motif-dropdown .motif-dropdown-menu .motif-dropdown-item {
				font-weight: 300;
				font-size: 1.2rem;
				line-height: 2.6rem;
				padding: 1rem;
			}

			& > a {
				color: ${lightColor[`white`]};
				outline: none;
				display: flex;
				align-items: center;
				&:hover {
					text-decoration: none;
				}
				.nav-bar__user-profile__user-name {
					display: inline-block;
					max-width: 25rem;
					width: auto;
					text-overflow: ellipsis;
					white-space: nowrap;
					overflow: hidden;
				}
				.caret {
					display: inline-block;
					width: 0;
					height: 0;
					vertical-align: middle;
					border-top: 0.4rem solid;
					border-right: 0.4rem solid transparent;
					border-left: 0.4rem solid transparent;
					margin: 1rem 1rem;
					transition: all 0.2s;
				}
			}
			&.nav-bar__user-profile--active {
				a .caret {
					transform: rotate(180deg);
				}
			}
		}

		.nav-bar__user-profile__menu {
			background-color: ${(props) => props.theme.color['grey225']};
			border: 0.1rem solid ${lightColor[`grey300`]};
			box-shadow: 0 0 2rem -1rem #222;
			width: 21rem;
			position: absolute;
			right: 4rem;
			top: 5rem;
			overflow: hidden;
			transition: all 0.3s;
			z-index: 9;
			font-size: 1.4rem;

			.nav-bar__user-profile__menu__links {
				display: block;
				padding: 1rem 1.5rem;
				color: ${lightColor[`grey500`]};
				border-top: 0.1rem solid #e5e5e5;
				cursor: pointer;
				text-decoration: none;
				:hover {
					text-decoration: underline;
				}
				&.userProfilePhoto {
					background: ${lightColor[`white`]};
					svg {
						width: 60%;
						height: 60%;
						max-width: 100%;
						max-height: 100%;
						margin: 0 auto;
						display: block;
					}
				}
			}
		}
	}
`;
